import { useState } from 'react';

type Props = {
  before: string;
  after: string;
  beforeLabel: string;
  afterLabel: string;
  alt: string;
  /** Tailwind aspect class matching the frame, e.g. aspect-video or aspect-[9/16]. */
  aspectClass?: string;
  className?: string;
  /** Above-the-fold instances should load eagerly. */
  eager?: boolean;
};

// Real before/after frames from the production pipeline. The two images share the same
// frame, so the "before" layer is clipped to the slider position while keeping full width.
export default function BeforeAfter({ before, after, beforeLabel, afterLabel, alt, aspectClass = 'aspect-video', className = '', eager = false }: Props) {
  const [position, setPosition] = useState(50);
  const clip = Math.min(Math.max(position, 0.5), 99.5);
  return (
    <div className={`relative select-none overflow-hidden rounded-xl bg-gray-900 ${aspectClass} ${className}`}>
      <img src={after} alt={`${alt}（${afterLabel}）`} loading={eager ? 'eager' : 'lazy'} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${clip}%` }}>
        <img
          src={before}
          alt={`${alt}（${beforeLabel}）`}
          loading={eager ? 'eager' : 'lazy'}
          draggable={false}
          className="absolute inset-y-0 left-0 h-full max-w-none object-cover"
          style={{ width: `${10000 / clip}%` }}
        />
      </div>
      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `calc(${clip}% - 1px)` }}>
        <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg">
          <i className="ri-arrow-left-right-line"></i>
        </div>
      </div>
      <span className="pointer-events-none absolute left-3 top-3 rounded bg-black/70 px-2 py-1 text-xs text-white">{beforeLabel}</span>
      <span className="pointer-events-none absolute right-3 top-3 rounded bg-black/70 px-2 py-1 text-xs text-white">{afterLabel}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={0.5}
        value={position}
        onChange={event => setPosition(Number(event.target.value))}
        aria-label={`${beforeLabel} / ${afterLabel}`}
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  );
}
